import React, { Fragment, useEffect, useState } from 'react';
import {
  Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Code, Type, Layers, FileText } from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { H4, Spinner } from '../../../../AbstractElements';
import { baseURL } from '../../../../Services/api/baseURL';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['blockquote'],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'blockquote', 'link',
];

const generateId = () => `sec_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const emptySection = () => ({
  _id: generateId(),
  title: '',
  content: '',
  contentType: 'richtext',
});

const PAGE_MODES = [
  { value: 'sections', label: 'Sections', icon: Layers },
  { value: 'html', label: 'Whole Page (HTML)', icon: Code },
  { value: 'text', label: 'Whole Page (Text)', icon: FileText },
];

const PolicyPageEditor = ({ pageType, pageTitle }) => {
  const [sections, setSections] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pageMode, setPageMode] = useState('sections');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem('token'));

  const fetchPage = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseURL}/api/content-settings/${pageType}`,
        { headers: { Authorization: `${getToken()}` } }
      );
      const data = res.data.data;
      if (data) {
        setTitle(data.title || pageTitle);
        setLastUpdated(data.updatedAt || null);
        setContent(data.content || '');
        setPageMode(data.pageMode || 'sections');
        if (data.sections && data.sections.length) {
          setSections(
            data.sections.map((s) => ({
              _id: s._id || generateId(),
              title: s.title || '',
              content: s.content || '',
              contentType: s.contentType || 'richtext',
            }))
          );
        } else if (data.content && !data.pageMode) {
          // Legacy single-blob page saved before container support — migrate it into one section.
          setSections([{ ...emptySection(), content: data.content }]);
        } else {
          setSections([]);
        }
      } else {
        setTitle(pageTitle);
        setContent('');
        setPageMode('sections');
        setSections([]);
      }
    } catch (err) {
      console.error(err);
      setTitle(pageTitle);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line
  }, [pageType]);

  const updateSection = (id, patch) => {
    setSections((prev) => prev.map((s) => (s._id === id ? { ...s, ...patch } : s)));
  };

  const addSection = () => {
    setSections((prev) => [...prev, emptySection()]);
  };

  const removeSection = (id) => {
    Swal.fire({
      title: 'Remove this section?',
      text: 'This will delete the section content once you save.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      confirmButtonColor: '#e53e3e',
      cancelButtonColor: '#aaa',
    }).then((result) => {
      if (result.isConfirmed) {
        setSections((prev) => prev.filter((s) => s._id !== id));
      }
    });
  };

  const moveSection = (index, direction) => {
    setSections((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title,
        pageMode,
        content: pageMode === 'sections' ? '' : content,
        sections: sections.map((s, i) => ({
          title: s.title,
          content: s.content,
          contentType: s.contentType,
          order: i,
        })),
        status: 'active',
      };
      const res = await axios.put(
        `${baseURL}/api/content-settings/${pageType}`,
        payload,
        { headers: { Authorization: `${getToken()}` } }
      );
      setLastUpdated(res.data.data?.updatedAt || new Date().toISOString());
      Swal.fire({
        title: `${pageTitle} saved!`,
        icon: 'success',
        confirmButtonColor: '#d3178a',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({ title: 'Failed to save', icon: 'error', confirmButtonColor: '#d3178a' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Container fluid style={{ paddingTop: '30px' }}>
        <Row>
          <Col sm="12">
            <Card style={{ borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <CardBody style={{ padding: '28px' }}>
                {/* Header */}
                <div
                  className="d-flex justify-content-between align-items-start mb-4"
                  style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px' }}
                >
                  <div>
                    <H4 style={{ margin: 0 }}>{pageTitle}</H4>
                    {lastUpdated && (
                      <p style={{ color: '#999', fontSize: '13px', margin: '4px 0 0' }}>
                        Last updated: {moment(lastUpdated).format('DD MMM YYYY, hh:mm A')}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleSave}
                    disabled={saving || loading}
                    style={{
                      background: '#d3178a',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: 600,
                    }}
                  >
                    <Save size={16} />
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <FormGroup className="mb-4">
                      <Label style={{ fontWeight: 600, fontSize: '14px' }}>Page Title</Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`e.g. ${pageTitle}`}
                        style={{ borderRadius: '8px', maxWidth: '500px' }}
                      />
                    </FormGroup>

                    <FormGroup className="mb-4">
                      <Label style={{ fontWeight: 600, fontSize: '14px' }}>Page Rendering Mode</Label>
                      <div className="d-flex" style={{ gap: '8px', flexWrap: 'wrap' }}>
                        {PAGE_MODES.map(({ value, label, icon: Icon }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setPageMode(value)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: pageMode === value ? '1px solid #d3178a' : '1px solid #ddd',
                              background: pageMode === value ? 'rgba(211,23,138,0.08)' : 'transparent',
                              color: pageMode === value ? '#d3178a' : '#555',
                            }}
                          >
                            <Icon size={14} />
                            {label}
                          </button>
                        ))}
                      </div>
                      <p style={{ marginTop: '8px', fontSize: '12px', color: '#aaa' }}>
                        {pageMode === 'sections'
                          ? 'Build the page from titled, reorderable containers.'
                          : pageMode === 'html'
                          ? 'Paste one raw HTML document — it renders on the customer-facing page exactly as pasted.'
                          : 'Write plain text for the whole page — it renders as-is, with no HTML formatting.'}
                      </p>
                    </FormGroup>

                    {pageMode === 'html' && (
                      <FormGroup className="mb-4">
                        <Label style={{ fontSize: '13px', fontWeight: 600 }}>Page HTML</Label>
                        <Input
                          type="textarea"
                          rows={12}
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          placeholder="<h1>Return Policy</h1><p>Write the full page as HTML…</p>"
                          style={{ borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px' }}
                        />
                        <div style={{ marginTop: '10px' }}>
                          <Label style={{ fontSize: '12px', color: '#999' }}>Preview</Label>
                          <div
                            style={{
                              border: '1px solid #eee',
                              borderRadius: '8px',
                              padding: '16px',
                              background: '#fcfcfc',
                            }}
                            dangerouslySetInnerHTML={{ __html: content }}
                          />
                        </div>
                      </FormGroup>
                    )}

                    {pageMode === 'text' && (
                      <FormGroup className="mb-4">
                        <Label style={{ fontSize: '13px', fontWeight: 600 }}>Page Text</Label>
                        <Input
                          type="textarea"
                          rows={12}
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          placeholder="Write the full page as plain text…"
                          style={{ borderRadius: '8px', fontSize: '13px' }}
                        />
                        <div style={{ marginTop: '10px' }}>
                          <Label style={{ fontSize: '12px', color: '#999' }}>Preview</Label>
                          <div
                            style={{
                              border: '1px solid #eee',
                              borderRadius: '8px',
                              padding: '16px',
                              background: '#fcfcfc',
                              whiteSpace: 'pre-wrap',
                              fontSize: '14px',
                            }}
                          >
                            {content}
                          </div>
                        </div>
                      </FormGroup>
                    )}

                    {pageMode === 'sections' && (
                      <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Label style={{ fontWeight: 600, fontSize: '14px', margin: 0 }}>
                        Sections
                      </Label>
                      <Button
                        onClick={addSection}
                        style={{
                          border: '1px solid #d3178a',
                          borderRadius: '8px',
                          padding: '6px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px',
                        }}
                      >
                        <Plus size={14} />
                        Add Section
                      </Button>
                    </div>

                    {sections.length === 0 && (
                      <div
                        style={{
                          padding: '40px',
                          color: '#999',
                          textAlign: 'center',
                          border: '1px dashed #ddd',
                          borderRadius: '8px',
                        }}
                      >
                        No sections yet. Click "Add Section" to create the first container for this page.
                      </div>
                    )}

                    {sections.map((section, index) => (
                      <div
                        key={section._id}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '10px',
                          marginBottom: '20px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          className="d-flex justify-content-between align-items-center"
                          style={{ background: '#fafafa', padding: '10px 16px', borderBottom: '1px solid #eee' }}
                        >
                          <span style={{ fontWeight: 600, fontSize: '13px', color: '#666' }}>
                            Section {index + 1}
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              title="Move up"
                              onClick={() => moveSection(index, -1)}
                              disabled={index === 0}
                              style={iconBtnStyle(index === 0)}
                            >
                              <ChevronUp size={15} />
                            </button>
                            <button
                              type="button"
                              title="Move down"
                              onClick={() => moveSection(index, 1)}
                              disabled={index === sections.length - 1}
                              style={iconBtnStyle(index === sections.length - 1)}
                            >
                              <ChevronDown size={15} />
                            </button>
                            <button
                              type="button"
                              title={section.contentType === 'html' ? 'Switch to rich text' : 'Switch to raw HTML'}
                              onClick={() =>
                                updateSection(section._id, {
                                  contentType: section.contentType === 'html' ? 'richtext' : 'html',
                                })
                              }
                              style={iconBtnStyle(false)}
                            >
                              {section.contentType === 'html' ? <Type size={15} /> : <Code size={15} />}
                            </button>
                            <button
                              type="button"
                              title="Remove section"
                              onClick={() => removeSection(section._id)}
                              style={{ ...iconBtnStyle(false), color: '#e53e3e' }}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>

                        <div style={{ padding: '16px' }}>
                          <FormGroup className="mb-3">
                            <Label style={{ fontSize: '13px', fontWeight: 600 }}>Container Title</Label>
                            <Input
                              value={section.title}
                              onChange={(e) => updateSection(section._id, { title: e.target.value })}
                              placeholder="e.g. Eligibility, Refund Timeline, How to Return an Item…"
                              style={{ borderRadius: '8px' }}
                            />
                          </FormGroup>

                          {section.contentType === 'richtext' ? (
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                              <ReactQuill
                                theme="snow"
                                value={section.content}
                                onChange={(val) => updateSection(section._id, { content: val })}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Write a passage or bullet points…"
                                style={{ minHeight: '160px' }}
                              />
                            </div>
                          ) : (
                            <>
                              <Label style={{ fontSize: '12px', color: '#999' }}>
                                Paste raw HTML — it will render below exactly as pasted.
                              </Label>
                              <Input
                                type="textarea"
                                rows={6}
                                value={section.content}
                                onChange={(e) => updateSection(section._id, { content: e.target.value })}
                                placeholder="<p>Paste or write HTML here…</p>"
                                style={{ borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px' }}
                              />
                              <div style={{ marginTop: '10px' }}>
                                <Label style={{ fontSize: '12px', color: '#999' }}>Preview</Label>
                                <div
                                  style={{
                                    border: '1px solid #eee',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    background: '#fcfcfc',
                                  }}
                                  dangerouslySetInnerHTML={{ __html: section.content }}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}

                    <p style={{ marginTop: '12px', fontSize: '12px', color: '#aaa' }}>
                      These sections will render container-by-container on the{' '}
                      <strong>/{pageType}</strong> page of the customer-facing site.
                    </p>
                      </>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

const iconBtnStyle = (disabled) => ({
  background: 'transparent',
  border: '1px solid #ddd',
  borderRadius: '6px',
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  color: '#555',
});

export default PolicyPageEditor;
